import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, Check, ArrowRight, ArrowLeft, Loader2, Sparkles } from "lucide-react";
import { CATEGORIES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  category: "",
  estimated_value: "",
  preferred_channel: "Whatnot",
  item_description: "",
  message: "",
};

const inputCls =
  "w-full bg-[#050505] border border-[#262629] text-white placeholder:text-white/30 px-4 py-3.5 text-sm focus:outline-none focus:border-[#3FE1D1] focus:ring-1 focus:ring-[#3FE1D1] transition-colors";

export default function Consignment() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileInput = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = (list) => {
    const incoming = Array.from(list).filter((f) => f.type.startsWith("image/"));
    setFiles((prev) => [...prev, ...incoming].slice(0, 8));
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const step0Valid = form.name.trim() && form.email.trim() && form.category;
  const step1Valid = form.item_description.trim().length > 5;

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("photos", f));
      await axios.post(`${API}/consignment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success("Submission received! Our team will reach out within 48 hours.");
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong. Please try again or email us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="consign"
      data-testid="consignment-section"
      className="relative bg-[#0a0a0b] py-24 md:py-32 border-t border-white/10 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-20 right-0 h-[500px] w-[500px] rounded-full bg-[#3FE1D1]/10 blur-[150px]" />

      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left — sticky manifesto */}
          <div className="lg:sticky lg:top-28 self-start">
            <span className="text-xs uppercase tracking-[0.3em] text-[#3FE1D1] font-medium">
              Consignment Program
            </span>
            <h2 className="mt-5 font-display font-extrabold uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.9]">
              Turn your <br />
              collection <span className="text-[#3FE1D1]">into cash.</span>
            </h2>
            <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-md">
              Send us your grails and we put them in front of 50,000+ buyers across
              Whatnot, TikTok and eBay. You set the reserve — we handle the sale,
              authentication and shipping.
            </p>

            <ul className="mt-8 space-y-4">
              {[
                "White-glove intake & authentication",
                "Exposure to 50,000+ active collectors",
                "You set reserves, we run the sale",
                "Fast payouts after items sell",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-white/75 text-sm md:text-base">
                  <span className="h-6 w-6 flex-shrink-0 bg-[#3FE1D1]/15 border border-[#3FE1D1]/40 flex items-center justify-center">
                    <Check size={13} className="text-[#3FE1D1]" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Right — form card */}
          <div className="bg-[#131315] border border-[#262629] p-7 md:p-10 relative">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="done"
                  data-testid="consignment-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="min-h-[420px] flex flex-col items-center justify-center text-center"
                >
                  <div className="h-16 w-16 bg-[#3FE1D1] flex items-center justify-center mb-6">
                    <Sparkles size={28} className="text-[#050505]" />
                  </div>
                  <h3 className="font-display font-extrabold uppercase text-white text-3xl tracking-tight">
                    You're in.
                  </h3>
                  <p className="mt-3 text-white/60 max-w-sm">
                    Thanks {form.name.split(" ")[0]} — your submission is with our
                    consignment team. Expect a reply within 48 hours.
                  </p>
                  <button
                    data-testid="consignment-reset"
                    onClick={() => {
                      setForm(EMPTY);
                      setFiles([]);
                      setStep(0);
                      setDone(false);
                    }}
                    className="mt-8 border border-[#3FE1D1] text-[#3FE1D1] font-bold uppercase text-xs tracking-widest px-6 py-3 hover:bg-[#3FE1D1]/10 transition-colors"
                  >
                    Submit another item
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {/* Progress */}
                  <div className="flex items-center gap-2 mb-8">
                    {[0, 1, 2].map((s) => (
                      <div
                        key={s}
                        className={`h-1 flex-1 transition-colors duration-300 ${
                          s <= step ? "bg-[#3FE1D1]" : "bg-white/10"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-6">
                    Step {step + 1} of 3 —{" "}
                    {["Your details", "The item", "Photos & review"][step]}
                  </p>

                  <AnimatePresence mode="wait">
                    {step === 0 && (
                      <motion.div
                        key="s0"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                      >
                        <input
                          data-testid="consign-name"
                          className={inputCls}
                          placeholder="Full name *"
                          value={form.name}
                          onChange={(e) => set("name", e.target.value)}
                        />
                        <input
                          data-testid="consign-email"
                          type="email"
                          className={inputCls}
                          placeholder="Email address *"
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                        />
                        <input
                          data-testid="consign-phone"
                          className={inputCls}
                          placeholder="Phone (optional)"
                          value={form.phone}
                          onChange={(e) => set("phone", e.target.value)}
                        />
                        <select
                          data-testid="consign-category"
                          className={`${inputCls} appearance-none`}
                          value={form.category}
                          onChange={(e) => set("category", e.target.value)}
                        >
                          <option value="" disabled>
                            Category *
                          </option>
                          {CATEGORIES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </motion.div>
                    )}

                    {step === 1 && (
                      <motion.div
                        key="s1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                      >
                        <textarea
                          data-testid="consign-description"
                          className={`${inputCls} min-h-[130px] resize-none`}
                          placeholder="Describe your item(s) — what is it, condition, any COAs? *"
                          value={form.item_description}
                          onChange={(e) => set("item_description", e.target.value)}
                        />
                        <input
                          data-testid="consign-value"
                          className={inputCls}
                          placeholder="Estimated value (optional) e.g. $500"
                          value={form.estimated_value}
                          onChange={(e) => set("estimated_value", e.target.value)}
                        />
                        <div>
                          <label className="text-xs uppercase tracking-[0.16em] text-white/40 mb-2 block">
                            Preferred sales channel
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {["Whatnot", "TikTok", "eBay", "No preference"].map((ch) => (
                              <button
                                key={ch}
                                data-testid={`consign-channel-${ch}`}
                                onClick={() => set("preferred_channel", ch)}
                                className={`px-4 py-2.5 text-xs uppercase tracking-wide border transition-colors ${
                                  form.preferred_channel === ch
                                    ? "bg-[#3FE1D1] border-[#3FE1D1] text-[#050505] font-bold"
                                    : "border-[#262629] text-white/70 hover:border-[#3FE1D1]/50"
                                }`}
                              >
                                {ch}
                              </button>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div
                        key="s2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                      >
                        <div
                          data-testid="consign-dropzone"
                          onClick={() => fileInput.current?.click()}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            onFiles(e.dataTransfer.files);
                          }}
                          className="border border-dashed border-[#262629] hover:border-[#3FE1D1] transition-colors cursor-pointer p-8 text-center"
                        >
                          <Upload className="mx-auto text-[#3FE1D1]" size={26} />
                          <p className="mt-3 text-white/70 text-sm">
                            Drag photos here or <span className="text-[#3FE1D1]">browse</span>
                          </p>
                          <p className="text-white/30 text-xs mt-1">Up to 8 images</p>
                          <input
                            ref={fileInput}
                            data-testid="consign-file-input"
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => onFiles(e.target.files)}
                          />
                        </div>

                        {files.length > 0 && (
                          <div className="grid grid-cols-4 gap-2">
                            {files.map((f, i) => (
                              <div key={i} className="relative aspect-square group">
                                <img
                                  src={URL.createObjectURL(f)}
                                  alt=""
                                  className="w-full h-full object-cover border border-[#262629]"
                                />
                                <button
                                  data-testid={`remove-photo-${i}`}
                                  onClick={() => removeFile(i)}
                                  className="absolute -top-2 -right-2 h-6 w-6 bg-[#050505] border border-white/20 flex items-center justify-center text-white hover:bg-[#3FE1D1] hover:text-black transition-colors"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <textarea
                          data-testid="consign-message"
                          className={`${inputCls} min-h-[80px] resize-none`}
                          placeholder="Anything else we should know? (optional)"
                          value={form.message}
                          onChange={(e) => set("message", e.target.value)}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Nav */}
                  <div className="flex items-center justify-between mt-8">
                    {step > 0 ? (
                      <button
                        data-testid="consign-back"
                        onClick={() => setStep((s) => s - 1)}
                        className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm uppercase tracking-wide transition-colors"
                      >
                        <ArrowLeft size={16} /> Back
                      </button>
                    ) : (
                      <span />
                    )}

                    {step < 2 ? (
                      <button
                        data-testid="consign-next"
                        onClick={() => setStep((s) => s + 1)}
                        disabled={step === 0 ? !step0Valid : !step1Valid}
                        className="inline-flex items-center gap-2 bg-[#3FE1D1] text-[#050505] font-bold uppercase text-sm tracking-[0.12em] px-7 py-3.5 hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        Continue <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        data-testid="consign-submit"
                        onClick={submit}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 bg-[#3FE1D1] text-[#050505] font-bold uppercase text-sm tracking-[0.12em] px-7 py-3.5 hover:bg-white transition-colors disabled:opacity-60"
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={16} className="animate-spin" /> Sending…
                          </>
                        ) : (
                          <>
                            Submit Consignment <ArrowRight size={16} />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
